import React, {
  ReactElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Switch, Text } from 'tea-component';
import CardSlot from '@/pages/dashboard/components/CardSlot';
import MiniTable from '@/pages/dashboard/components/MiniTable';
import SvgIcon from '@/pages/home/components/SvgIcon';
import {
  exportExcel,
} from '@/pages/dashboard/utils/utils';
import {  useDblIntl } from '@/pages/datalab/utils/utils';
import {
  ColumnsInfo,
  ITableInfo,
} from '@/pages/datalab/pages/BehaviorAnalysis/GptAnalysis/interface';
import Chart from '@/pages/dashboard/components/Chart';
import ChartFilter from '@/pages/datalab/pages/BehaviorAnalysis/GptAnalysis/Chat/components/ChartMessage/ChartFilter';
import { COLORS, CHART_TYPE_ICON, chartOptions } from './constants';
import { yAxisFormat } from '@/pages/dashboard/components/Chart/_config/utils';
import { useDebounceFn, useRafState,  } from 'ahooks';
import {  cloneDeep, filter, find, flattenDeep, get, isArray, isEmpty, sortBy,} from 'lodash-es';
import CloumsCenter from '@/pages/intelligence/components/TableComponents/components/CloumsCenter';
import { ChartLink, MultipleGameMetricsLink } from '../Message';
import { ChartRef } from '@/pages/dashboard/components/Chart/_config/interface';
import { adjustNumber, computeChartHeight, excelConversionFormat, filterDataByName, formatLegendAndPictureCoexist, formatOfType, getTop10HeatPictures, identifyGapsAndMarkPoints, mergeDataIfAllElementsHaveOneElement, tooltipFormatter } from '../../utils';
import './index.scss';
export default function (props: any) {
  const {
    chartData,
    pieData,
    chartTypes = [],
    fileInfo,
    legends,
    metrics = [],
    // entities,
    // sources,
    tableInfo,
    chartTitle,
    chartIcon,
    // chartUrl,
    entityInfos,
    sourceInfos,
    // metricInfos,
    language,
  } = props;
  const newChartData: any = {
    line: chartData,
    bar: chartData,
    pie: pieData,
    ranking: chartData,

  };
  // console.log(props);
  const { dblFormatMessage: gptFormatMessage } = useDblIntl('gpt');
  const [noData, setNoData] = useState(false);
  const [chartHeight, setChartHeight] = useState(360);

  // 当前图表类型
  const [activeType, setActiveType] = useState<string>(chartTypes[0]);

  // 筛选器选项
  const [filters, setFilters] = useState({
    metric: '',
    entity: '',
    source: '',
  });
  // 
  const signOneCatch = useRef<{ [key in string]: string | number }>({
    count: 0,
    name: '',
  });
  // 图例选项
  const [lengedGameImages, setLengedGameImages] = useRafState<any[]>([]);
  // 变化率
  const [rateSort, setRateSort] = useRafState<boolean>(false);
  const chartRef = useRef();

  // 筛选器有变动的时候
  useEffect(() => {
    try {
      if (chartRef?.current?.instance) {
        const { entity, metric, source } = filters;
        const entityDim = entity
          ? { dimension: entityInfos[entity].column, value: entity }
          : undefined;
        const sourceDim = source
          ? { dimension: sourceInfos[source].column, value: source }
          : undefined;
        const metricOpt = metrics?.find(({ value }) => value === metric) || {};
        const opt = getNewChartOptions({
          data: newChartData[activeType],
          legends,
          entity: entityDim,
          source: sourceDim,
          metricOpt,
          type: activeType,
          language,
          chartRef: chartRef.current,
          rateSort
        });
        if (opt) {
          chartRef.current
            ?.instance()
            ?.setOption(opt, { notMerge: true, lazyUpdate: false }); // 用 useLayoutEffect 图表就不会更新option, 也不展示图形
          // 图例点击
          opt?.legend?.length > 2 &&
            chartRef.current
              ?.instance()
              ?.on('legendselectchanged', handleOnChangeLegend);
          signOneCatch.current.count = 0;
       
          // 游戏图片展示
          setLengedGameImages(!isEmpty( opt?.legend) &&
          opt?.legend.length > 1 &&
            opt?.legend?.map((task) => ({
              name: task?.data?.[0]?.name.split('_')[1],
              image: task?.data?.[0]?.coverUrl.split('image://')[1],
            })) || []);
          opt.chartHeight !== chartHeight && setChartHeight(opt.chartHeight);
          noData && setNoData(false);
        } else {
          setNoData(true);
        }
      }
    } catch (e) {
      console.error('图表数据筛选错误', e);
    }
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      chartRef.current?.instance()?.off('legendselectchanged');
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, activeType, rateSort]);

  useEffect(() => {
    chartRef.current?.instance()?.resize();
  }, [chartHeight]);

  // 图例点击
  const handleOnChangeLegend = (params?: any) => {
    const { count, } = signOneCatch.current;
    const selectedConfig = cloneDeep(params.selected);
    // 获取点击图例的选中状态
    const isSelected = params.selected[params.name];
    // eslint-disable-next-line no-plusplus
    signOneCatch.current.count++;
    chartRef.current?.instance()?.setOption({
      legend: {
        selected: { ...selectedConfig, ...{ [params.name]: isSelected } },
      },
    });
    // TODO:下面这个if判断,是为了额外处理echarts多个图例seleced设置为 false时,点击事件,会需要点击2次,才能生效.这是遗留的bug,暂时没有思路,先这样处理,额外多了一些冗余的代码沉淀在这块
    // 如果是第一次，点击的是seleced为flase的，需要强制转高亮状态
    if (count === 0 && !isSelected) {
      chartRef.current?.instance()?.dispatchAction({
        type: 'legendSelect',
        name: params.name,
      });
    }
  };

  const { chartMap, iconList } = useMemo(() => {
    const chartMap: { [key: string]: ReactElement; } = {};
    const iconList: { icon: string; }[] = [];
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const ChartComponent = createChartComponent(chartRef);
    chartTypes.forEach((type: string) => {
      // type 不存在
      if (!CHART_TYPE_ICON[type]) return;

      // 获取对应type的icon
      const icon = CHART_TYPE_ICON[type];
      // 切换图标
      iconList.push({ icon });

      if (type !== 'table') {
        chartMap[type] = ChartComponent;
        return;
      } 
      chartMap[type] = createTableComponent(tableInfo, language);
      
      // 后端有时会返回 折线图类型, 但是不返回折线图数据这种类似的问题, 这种时候前端不展示对应的图表
      // if (!props[paramString]) return;
    });

    return { chartMap, iconList };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartTypes]);

  // 文件下载
  const { run: downloadFileWithAuth } = useDebounceFn(
    (fileName: string, tableOption: any) => {
      const [excelData] = excelConversionFormat(tableOption);
      exportExcel(fileName, excelData);
    },
    {
      wait: 500,
    },
  );

  // 联网第三方链接
  const cacheGameData = useMemo(() => {
    if (isEmpty(props.metrics_urls) && !isArray(props.metrics_urls)) return [];
    const { entity, metric, source } = filters;
    const str = `${entity}_${source}_${metric}`;
    const defaultPathName= gptFormatMessage('related_page');
    const filteredStr = str
      .split('_')
      .filter((item) => item !== '' && item !== 'null' && item !== 'undefined')
      .join('_');
   
    if (activeType === 'ranking') {
      const rankIngDataSource=props.metrics_urls.map((task: { name: string; })=>({
        ...task,
        name: `${defaultPathName}${task.name}`
      }));
      return filterDataByName(rankIngDataSource, filteredStr);
    }

    const getGameMetrics = get(
      find(metrics, { value: metric }),
      'text',
      '',
    );

    const cloneDeepMetricsUrls = cloneDeep(props.metrics_urls);
    const metricsDataSource = cloneDeepMetricsUrls.map((task: { name: { split: (arg0: string) => [any, any, any]; }; }) => {
      const [gameName, gameMetrics, gameSource] = task.name.split('-');
      return {
        ...task,
        name: `${defaultPathName}${gameSource ? `${gameName  }-` : ''}${getGameMetrics}-${gameSource || gameMetrics}`
      };
    });
    return filterDataByName(metricsDataSource, filteredStr);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeType, filters, metrics, props.metrics_urls]);

  return (
    <CardSlot
      getChange={(icon: string) => {
        setActiveType(CHART_TYPE_ICON[icon]);
      }}
      title={
        <>
          <SvgIcon type={chartIcon} />
          <ChartLink
            {...{
              gptFormatMessage,
              chartUrl: undefined,
              chartTitle,
            }}
          />
        </>
      }
      className="db-icon-hover"
      iconList={iconList}
      isEyes={true} // 为true的时候才不显示小眼睛图标
      activeTab={CHART_TYPE_ICON[activeType]}
      onDownload={() => {
        downloadFileWithAuth(fileInfo?.name, tableInfo);
      }}
    >
      <ChartFilter
        {...props}
        activeType={activeType}
        onChange={(value: React.SetStateAction<{ metric: string; entity: string; source: string; }>) => setFilters(value)}
        value={filters}
      />
      {
        activeType==='ranking' && <div className="db-filter-rate">
          <span>{gptFormatMessage('rate_of_change')} </span>
          <Switch value={rateSort} onChange={(value)=>setRateSort(value)}/>
        </div>
      }
      <div
        style={{
          display: noData && activeType !== 'table' ? 'none' : 'block',
          height: activeType === 'table' ? '100%' : chartHeight,
          marginBottom: activeType === 'table' && '5px',
        }}
        className="db-legends-container"
      >
        {chartMap[activeType]}
        {/* 这里通过定位的形式来实现左侧增加图片，翻遍api没找到相关实现， */}
        {activeType !== 'table' && (
          <div
            style={{
              position: 'absolute',
              left: '120px',
              bottom: '2px',
            }}
          >
            {lengedGameImages?.map((task) => (
              <div key={task.name} className="db-legend-position-item">
                {task.image && <img src={task.image} />}
                <Text parent="div" style={{ maxWidth: 100 }} overflow tooltip>
                  <span>{task.name}</span>
                </Text>
              </div>
            ))}
          </div>
        )}
      </div>

      <div
        style={{
          display: noData && activeType !== 'table' ? 'flex' : 'none',
          height: chartHeight,
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        {language === 'en'
          ? 'No data under current filter selections.'
          : '当前筛选条件下无数据。'}
      </div>
      {!isEmpty(cacheGameData) && (
        <MultipleGameMetricsLink
          {...{ list: cacheGameData, gptFormatMessage }}
        />
      )}
    </CardSlot>
  );
}
// 渲染图表的核心方法，目前没有一个较好的设计模式，业务代码一对多的关系处理，导致代码极其难以维护，待重构这一层，方便后续开发
function getNewChartOptions({
  data,
  legends,
  entity,
  source,
  metricOpt,
  type,
  language,
  chartRef,
  rateSort
}: any) {
  const {
    x_key: xKey,
    // text: metricText,
    // value,
    value: metric,
    column_type: columnType,
  } = metricOpt;
  // debugger;
  if (!xKey) return null;
  // x轴数据
  const xAxisDataSet = new Set();
  const isBarChartType = type === 'bar';
  const isRankIngChartType = type === 'ranking';
  // 用平台 和 数据源筛选项 过滤数据
  let  filterData = data?.filter((item: { [x: string]: unknown; }) => {
    const hasEntity = entity ? item[entity.dimension] === entity.value : true;
    const hasSource = source ? item[source.dimension] === source.value : true;
    const result = hasEntity && hasSource;
    result && item[xKey] && xAxisDataSet.add(item[xKey]);
    return result;
  });
  if (isRankIngChartType) {
    // 当是top10时，指标的维度不一，这里根据游戏名过滤name，有待欠缺，直接根据映射的指标过滤掉空数据
    filterData = filter(filterData, item => !isEmpty(item?.[metric]));
    if(rateSort){
      filterData=filterData.map(task=>({
        ...task,
        [`${metric}_radio`]: task?.[`${metric}_radio`]||'0'
      }));
    }
    console.log(filterData);
  }
  if (!filterData?.length) {
    // 说明没有数据
    return null;
  }

  const dataset: any[] = [];
  // 获取 legend 和 series, 以及对应的筛选配置
  let legend: any[] = [];
  let series: any[] = [];
  const isCompare = legends?.length > 1; // 是否有对比项
  let lengedDefaultDisabled: { [key in string]: boolean } = {};
  let singleDimension = false;
  if (legends?.length > 0) {
    // 前端接收数据的时候有做数据处理, 这块应该是必然大于0的
    // legends 数组中每个元素的信息是 一条对比项的的信息, 包含 对比项的column 和 name, 以及在这条对比项下的图例的column
    legends?.forEach((item: any) => {
      const {
        legend_column: legendColumn,
        icon,
        // data,
        compare_column: compareColumn,
        compare_name: compareName = '',
      } = item;
      const userTriggeredImgType = get(
        find(icon, { game_type: entity?.value }),
        'img_url',
        '',
      );
      const compareNames: string[] = compareName?.split('$~') || [];
      const tempOfDataset: any[] = [
        {
          fromDatasetIndex: dataset.length,
          transform: [
            {
              type: 'sort',
              config: {
                // 这里需要做下区分，如果是top10,需要根据指标数据来做排序，而不是日期
                // eslint-disable-next-line no-nested-ternary
                dimension: isRankIngChartType ? rateSort? `${metric}_radio` :metric : xKey,
                order: 'asc',
                parser: xKey === 'date' && !isRankIngChartType ? 'time' : 'number',
              },
            },
          ],
        },
      ];
      const legendsData: string[] = []; // 记录图例名称
    
      // TODO:待服务端开启排序功能
      // if (!isRankIngChartType) {
      //   filterData = sortBy(filterData, (o) => o[legendColumn[itemIndex || 0]]);
      // }
     
      legend.push({
        ...chartOptions.legend,
        data: [],
        top: 350 + legend.length * 20,
        left: isCompare  ? 250 : 'center',
        selector: undefined,
        formatter: (value: string) => {
          const [gameType, gameTitle] = value?.split('_');
          if (!isEmpty(legends) && legends.length <= 1 &&!isNameSuffix) {
            return gameType + (gameTitle ? `_${  gameTitle}` : '');
          }
          return gameType;
        },
      });
      // 1. 手动筛选对比项的数据源source (逻辑包含没有对比项的情况)
      // debugger
      const compareFilterData = filterData.filter((item: any) => {
        let result = true;
        compareNames.forEach((name = '', index: number) => {
          const value = item[compareColumn?.[index]];
          result = result && (value ? value === name : true); // value 不存在说明后端没有返回值, 是没有对比项的情况(有时没有对比项也会返回值), 直接返回true; 存在则需要和name进行比较.
        });
        if (result) {
          // 如果数据存在计算图例名称
          const and: any[] = [];
          const legendName = legendColumn
            ?.reduce((acc: string, column: string) => {
              const name = item[column] || '';
              and.push({ dimension: column, '=': name });
              return name ? `${acc  }，${name}` : acc; // 返回 legend的名称
            }, '')
            .slice(1); // 拼接名称
          if (!legendsData.includes(legendName) && item[metric]) {
            // 之前没有保存过的图例名称
            legendsData.push(legendName);
            const compareIndex = legend?.length - 1; // legend中有几个数组元素, 就是有几个对比项
            const color = COLORS[compareIndex % 5][(legendsData.length - 1) % 10];
            // 1.1 保存 图例数据
            if (legendName) {
              legend[compareIndex]?.data?.push({
                name: legendName
                  ? `${`${legendName  }_`}${compareName || ''}`
                  : legendName,
                // itemStyle: { color },
                coverUrl: userTriggeredImgType
                  ? `image://${  userTriggeredImgType}`
                  : '',
              });
              // 针对图例进行排序
              legend[compareIndex].data = sortBy(legend[compareIndex]?.data, (o) => o?.name);
            }
              
            // 1.2 记录每个图例对应的 series的筛选条件设置(dataset中的配置条件)
            and.length > 0 &&
              tempOfDataset.push({
                fromDatasetIndex: dataset.length + 1,
                transform: {
                  type: 'filter',
                  config: {
                    and,
                  },
                },
              });

            // 1.3 计算 series 配置
            series.push({
              id: `${`${legendName  }_`}${compareName || ''}`, // id 和 name, 在显示浮窗取值时(formatter方法), 用name截取id, 获取到对比项的名称
              name: legendName
                ? `${`${legendName  }_`}${compareName || ''}`
                : legendName, // 需要和legend配置中的name一一对应
              datasetIndex: tempOfDataset.length + dataset.length,
              type: isRankIngChartType ? 'bar' : type,
              encode: {
                [isRankIngChartType ? 'y' : 'x']: xKey,
                [isRankIngChartType ? 'x' : 'y']: metric,
                // x: xKey,
                // y: metric,
              },
              stack: (isBarChartType||isRankIngChartType) && type + compareIndex,
              showSymbol: !!((isBarChartType||isRankIngChartType)), // 只有在 tooltip hover 的时候显示symbol
              symbolSize: 10,
              lineStyle: { color },
              itemStyle: {
                normal: {
                  color,
                  borderColor: '#fff',
                  borderWidth: 0
                }
              },
              symbol: 'circle',
              emphasis: {
                itemStyle: {
                  borderWidth: 1,
                  color,
                },
              },
              label: {
                show: !!isRankIngChartType,
                formatter: (params: { value: { [x: string]: string | number; }; }) => {
                  if (!isRankIngChartType) return '';
                  return formatOfType(`${columnType  }&chart`, params.value[metric]);
                },
                position: 'right',
              },
            });
          }
        }
        return result;
      });
   
         
      // 2. 图表配置项 dataset
      compareFilterData?.length > 0 &&
        dataset.push({ source: compareFilterData }, ...tempOfDataset); // 添加当前对比项的数据源
      legendsData.length === 0 && legend.pop();
    });
    // 格式化图例规范
    // 如果legend小于1,并且isNameSuffix为真，说明这个图例是单游戏，维度不限(场景太多不固定)
    // 如果legend小于1,并且isNameSuffix为假，说明这个图例是多游戏，单维度
    // 如果legend大于1,并且isNameSuffix为真，说明这个图例是多游戏，维度不限(场景太多不固定)
    const [gameDimension, isNameSuffix] = mergeDataIfAllElementsHaveOneElement(legend);
    legend = gameDimension;
    singleDimension=isNameSuffix;
    if (legend?.length === 1 && !isEmpty(legend?.[0].data)) {
      legend[0].left = 'center';
    }
    // 3. 图表图例只有一行,需要特殊处理
    if ((legend?.length === 1 && (!isNameSuffix || legend[0].data.length === 1))) {
      // 图例重置基础色
      const newSeries = series.map((task, taskIndex,) => {
        const colors =
          COLORS[0 % 5][taskIndex % 10];
        return {
          ...task,
          lineStyle: { color: colors },
          'emphasis': {
            'itemStyle': {
              'borderWidth': 1,
              'color': colors
            }
          },
          'itemStyle': {
            'normal': {
              'color': colors,
              'borderColor': '#fff',
              'borderWidth': 0
            }
          },
        };
      });
      series=flattenDeep(newSeries);
      // 图例增加图片
      legend?.forEach((task,) => {
        if ( !isEmpty(task.data)) {
          // eslint-disable-next-line array-callback-return
          task.data.map((item, index) => {
            // 主要应用与图例与图片共存
            formatLegendAndPictureCoexist(legend, index, item.coverUrl.split('image://')[1]);
          });
        }
      });
    }
    // 3. 图表图例超出2行，将默认置灰
    if (legend?.length > 1) {
      legend.forEach((task, index) => {
        if (index > 1) {
          lengedDefaultDisabled = {};
          const {data} = task;
          // eslint-disable-next-line no-param-reassign
          task.selected = lengedDefaultDisabled;
          data.forEach((item: { name: string | number; }) => {
            lengedDefaultDisabled[item.name] = false;
          });
        }
      });
    }
    if(!isRankIngChartType && !isBarChartType) {
      series[0].markPoint = {
        symbol: 'circle',
        symbolSize: 10,
        data: identifyGapsAndMarkPoints({
          dataList: filterData,
          key: metric,
          seriesColor: series,
          legend_column: legends?.[0]?.compare_column?.[0],
          sortArray: [
            ...(legends?.[0]?.legend_column || ''),
            ...(legends?.[0]?.compare_column || ''),
            'date',
          ],
        }),
      };
    }
  
  }
  // else {
  //   // 没有返回图例的 配置
  //   dataset.push({ source: filterData });
  //   series.push({
  //     type,
  //     encode: {
  //       x: xKey,
  //       y: metric,
  //     },
  //   });
  // }

  // TODO xAxis 的data可能需要调整
  const xAxis = {
    ...chartOptions.xAxis,
    type: isRankIngChartType ? 'value' : 'category',
    data: Array.from(xAxisDataSet).sort((a, b) =>
      a?.localeCompare?.(b, undefined, { numeric: true }),
    ),
    axisPointer: {
      type: (isBarChartType||isRankIngChartType) ? 'shadow' : 'line',
    },
    interval: isRankIngChartType ? null : 1,
    axisLabel: {
      ...chartOptions.xAxis.axisLabel,
      formatter: (value: number) => {
        if (isRankIngChartType) {
          return yAxisFormat(value, columnType === 'percent', 0);
        }
        return value;
      },
    }
  };
  const yAxis = {
    ...chartOptions.yAxis,
    type: isRankIngChartType ? 'category' : 'value',
    // y轴刻度top10点击
    triggerEvent: !!isRankIngChartType,
    axisLabel: {
      formatter: (value: number| string, index: number) => {
        if (isRankIngChartType) {
          const maxLength = 10;
          let truncatedStr = value.substring(0, maxLength);
          if (value.length > maxLength) {
            truncatedStr += '...';
          }
  
          return `{img${index}|}  ${truncatedStr}`;
          // return `{img${index}|} ${value}`;
        }
        return yAxisFormat(value, columnType === 'percent', 0);
      }

    }
  };
  
  if (metric === 'rank' && type==='line') {
    yAxis.inverse = true;
    yAxis.min= 1;
  }
  if (isRankIngChartType) {
    const dataSource = dataset?.[0]?.source;
    const maxRightLength = dataSource?.[0]?.[metric];
    const max=  adjustNumber((maxRightLength * 1.1).toFixed(0));
    const isAllMinimalNum= dataSource.every((res: any)=>res?.[metric] <  0);
    // 右侧宽度
    xAxis.min = 0;
    xAxis.max = +max > 0 ? max : undefined;
    xAxis.interval= +max / 5 > 0   ? +max / 5  : undefined;
    if(isAllMinimalNum){
      xAxis.min = undefined;
      xAxis.inverse= true; // 翻转坐标轴的显示方向
    }
    chartOptions.grid.left = '4';
    // TODO:需要一个方法,来动态计算左侧y轴文本的距离
    if (dataSource.length !== 1) {
      yAxis.axisLabel.align = 'left';
      yAxis.axisLabel.inside = true;
      yAxis.axisLabel.margin = '-120';
      chartOptions.grid.height= '320';
      // TODO:待判断左右宽度
      // 左侧宽度
      chartOptions.grid.left = '120';
      if (dataSource.length > 10) chartOptions.grid.height = '410';
    }
    chartOptions.grid.right = 20;
    yAxis.axisTick = { show: false };
    // y轴刻度增加图片
    yAxis.axisLabel.rich = getTop10HeatPictures(dataset?.[0]?.source, rateSort ? `${metric}_radio` : metric );
    // 排名图y轴点击关闭，防止触发多次
    chartRef
      ?.instance()
      ?.off('click');
    // 排名图y轴点击
    chartRef
      ?.instance()
      ?.on('click', (params) => {
       
        if (params.targetType === 'axisLabel' && !isEmpty(dataSource) ) {
          const top10DetailUrl = get(
            find(dataSource, { game_name: params.value }),
            'url',
            '',
          );
          window.open(`${window.location.origin  }/${  top10DetailUrl}`);
        }
      });
  } else {
    chartOptions.grid.left=4;
    chartOptions.grid.right = 22;
    chartOptions.grid.height= '320';
  }

  if (!isEmpty(legend) && isArray(legend)) {
    if (legend[0].data.length > 15) {
      chartOptions.tooltip.enterable = true;
    } else {
      chartOptions.tooltip.enterable = false;
    }
  }
  return {
    ...chartOptions,
    legend,
    series,
    dataset,
    tooltip: {
      ...chartOptions.tooltip,
      axisPointer: (isBarChartType||isRankIngChartType)
        ? {
          type: 'shadow',
          shadowStyle: {
            color: '#d8dae4',
            width: '1',
            opacity: 0.2,
          },
        }
        : chartOptions.tooltip.axisPointer,
      formatter: (params: any) => tooltipFormatter({
        params,
        metricOpt,
        language,
        legendArr: legend,
        isRankIngChartType,
        singleDimension,
        metricsType: metric,
        rateSort
      }),
    },
    yAxis,
    xAxis,
    chartHeight: computeChartHeight(legends, legend?.length, isRankIngChartType ,dataset?.[0]?.source?.length),
  };
}

// 创建折线图
function createChartComponent(ref: React.MutableRefObject<undefined> | React.Ref<ChartRef> | undefined) {
  return <Chart ref={ref} option={{}} notMerge={true} />;
}

// 创建表格
// TODO:单独针对表格进行定制化开发，后续挪出本文件
function createTableComponent(data: ITableInfo) {
  const { columnsInfo = [], data: tableData = [] } = data;
  // const isEn = language === 'en';
  const columns = columnsInfo?.map((info: ColumnsInfo, index: number) => ({
    key: info.origin_name,
    header: (
      <CloumsCenter
        width={index === 0 ? '100%' : 170}
        align={index === 0 ? 'left' : 'right'}
        style={{
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {info.name}
      </CloumsCenter>
    ),
    width: 195,
    render(record: { [x: string]: string | number; }) {
      return (
        <CloumsCenter
          width={index === 0 ? '100%' : 185}
          align={index === 0 ? 'left' : 'right'}
          style={{
            maxWidth: 185,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {formatOfType(
            `${info.column_date_type  }&table`,
            record[info.origin_name],
          ) || '-'}
        </CloumsCenter>
      );
    },
  }));

  // const columns2 = columnsInfo?.map((info, index) => {
  //   return {
  //     width: 170,
  //     features: { sortable: (first: any, second: any)=>{
  //         const a = first || '-';
  //         const b = second || '-';
  //         const numA = Number(a);
  //         const numB = Number(b);
  //         if (isNaN(numA) || isNaN(numB)) return a.localeCompare(b);
  //         return numA - numB;
  //     } },
  //     code: info?.origin_name,
  //     name: info.name,
  //     align: index === 0 ? 'left' : 'right',
  //     render(index,record,arr) {
  //       return (
  //         formatOfType(
  //           info.column_date_type + '&table',
  //           record[info.origin_name],
  //         ) || '-'
  //       );
  //     },
  //   };
  // });
  return (
    <MiniTable
      loading={false}
      height={340+100}
      tableOption={columns}
      tableList={tableData}
      className={'chart-message-table'}
      scrolLableVal={{
        virtualizedOptions: {
          height: 400,
          itemHeight: 50,
        },
      }}
      tableConfig={{
        isSortable: true,
        disableChangeSort: false,
        setSortableConfig: (sorts: any, setSorts: any) => ({
          columns: columns.map((e: any) => ({
            key: e.key,
            prefer: 'desc',
            sorter: (first: any, second: any) => {
              // 尝试将字符串转换成数字
              const a = first[e.key] || '-';
              const b = second[e.key] || '-';
              const numA = Number(a);
              const numB = Number(b);
              if (isNaN(numA) || isNaN(numB)) return a.localeCompare(b);

              return numA - numB;
            },
          })),
          value: sorts,
          onChange: (value: any, context: any) => {
            setSorts([context?.sort]);
          },
        }),
      }}
    />
    // <>
    //   <Profiler id="GlobalCommonBaseTable">
    //     <CommonBaseTable dataSource={tableData} columns={columns2}
    //       tableConfig={{
    //         isShowPagination: false,
    //         tableHeight: 340,
    //         estimatedRowHeight:50,
    //         primaryKey:'origin_name',
    //         isOpenInitialSort:true,
    //       }}
    //       featConfig={
    //         {
    //           // customFeatKey:{serialNumberHandler},
    //           // sort: {
    //           //   mode: 'single',
    //           //   onChangeSorts: (params) => {
    //           //   },
    //           // },
    //           // multiSelect:{
    //           //   checkboxPlacement: 'start',
    //           //   checkboxColumn: { lock: true },
    //           //   clickArea: 'checkbox',
    //           // }
    //         }
    //       }
    //        />
    //   </Profiler>
    //   <Profiler id="MiniTable">
    //     <MiniTable
    //       loading={false}
    //       height={340}
    //       tableOption={columns}
    //       tableList={tableData}
    //       className={'chart-message-table'}
    //       scrolLableVal={{
    //         virtualizedOptions: {
    //           height: 296,
    //           itemHeight: 50,
    //         },
    //       }}
    //       tableConfig={{
    //         isSortable: true,
    //         disableChangeSort: false,
    //         setSortableConfig: (sorts: any, setSorts: any) => ({
    //           columns: columns.map((e: any) => ({
    //             key: e.key,
    //             prefer: 'desc',
    //             sorter: (first: any, second: any) => {
    //               // 尝试将字符串转换成数字
    //               const a = first[e.key] || '-';
    //               const b = second[e.key] || '-';
    //               const numA = Number(a);
    //               const numB = Number(b);
    //               if (isNaN(numA) || isNaN(numB)) return a.localeCompare(b);

  //               return numA - numB;
  //             },
  //           })),
  //           value: sorts,
  //           onChange: (value: any, context: any) => {
  //             setSorts([context?.sort]);
  //           },
  //         }),
  //       }}
  //     />
  //   </Profiler>
  // </>
  );
}
